/**
 * ONE PLAN LINE, ONE WRITE -- the save handling behind the plan-row controls.
 * Used by the consult row for decision, assignment and remark saves.
 *
 * WHY IT IS A HOOK AND NOT THREE HANDLERS. A plan line carries three facts a
 * doctor changes -- the DECISION, the PERFORMING DOCTOR and the REMARK -- and
 * all three go through the one audited endpoint. Written as three handlers they
 * drifted, and the drift was the defect: the assignment had no failure path at
 * all, did not disable its control while a write was unanswered, and updated
 * from a row captured at render -- so a reply that landed after a decision
 * confirmed could erase it. Everything below exists to make those three facts
 * impossible to get differently right.
 *
 * THE FOUR RULES IT ENFORCES, all of them on ONE line at a time:
 *
 *   1. ONE WRITE IN FLIGHT. A second commit while one is unanswered is
 *      REFUSED, and the refusal is recorded rather than swallowed. The guard is
 *      a REF, not state, because two events in the same tick both read state
 *      one render behind -- which is exactly what a double-click is.
 *   2. EVERY CONTROL GOES BUSY. `inFlight` is non-null for the whole line, so
 *      the caller disables the picker, the remark and the decision together. A
 *      control left live under an unanswered write is how two writes start.
 *   3. THE ROW IS RECONCILED, NOT PATCHED. A reply carries the WHOLE line, so
 *      the whole line is adopted from `rowRef` -- never from a row captured in
 *      a closure when the handler was created.
 *   4. A REPLY THAT IS NO LONGER THE LATEST IS DROPPED. Each write takes a
 *      monotonic ticket. With rule 1 holding this can never fire; it is the
 *      second fence, for a reply that outlives the write that asked for it (a
 *      re-mount, a reset), and it is counted so its silence is visible.
 *
 * WHAT IT DOES NOT DO: the write itself. `write` is the caller's -- the
 * existing audited `updatePlanItem` -- so this hook introduces no endpoint, no
 * second writer and no fetch. It also decides nothing about COPY: a failure is
 * classified by `classifyPlanWriteFailure` and the words belong to the
 * controls.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  classifyPlanWriteFailure,
  type PlanDecisionOutcome,
} from '@/dashboard/doctor/PlanDecisionPicklist';

/** The three facts a doctor changes on one plan line. */
export type PlanWriteKind = 'decision' | 'assignment' | 'remark' | 'price';

export const PLAN_WRITE_KINDS: readonly PlanWriteKind[] = [
  'decision',
  'assignment',
  'remark',
  'price',
];

/** The outcome of each fact's last attempt. Idle until one is attempted. */
export type PlanLineOutcomes = Readonly<Record<PlanWriteKind, PlanDecisionOutcome>>;

const IDLE: PlanLineOutcomes = {
  decision: { kind: 'idle' },
  assignment: { kind: 'idle' },
  remark: { kind: 'idle' },
  price: { kind: 'idle' },
};

/** A write REFUSED because the line already had one in flight. */
export interface PlanWriteRefusal {
  readonly kind: PlanWriteKind;
  readonly because: PlanWriteKind;
}

export interface PlanLineWrite<TRow> {
  /** The LAST CONFIRMED row -- what a server response actually carried. */
  readonly row: TRow;
  /** Which fact is being written, or null. Pass to every control as `busy`. */
  readonly inFlight: PlanWriteKind | null;
  readonly outcomes: PlanLineOutcomes;
  /** Commit one fact. True only when its refreshed row was confirmed.
   * Existing void callers may ignore the result; V14g stage saves join it. */
  readonly run: (kind: PlanWriteKind, patch: unknown) => Promise<boolean>;
  /** Refusals and dropped replies, in order. Diagnostics, not state. */
  readonly refused: readonly PlanWriteRefusal[];
  readonly stale: readonly PlanWriteKind[];
  readonly clearOutcomes: () => void;
}

/**
 * @param initial  the stored row, off the wire.
 * @param write    the caller's existing audited write. It must resolve with the
 *                 refreshed parent view, or reject.
 * @param findRow  pull THIS line out of that refreshed view. Returning
 *                 undefined means the reply did not carry this row, which is
 *                 `unconfirmed` -- NOT a save.
 */
export function usePlanLineWrite<TRow, TView>(params: {
  readonly initial: TRow;
  readonly write: (kind: PlanWriteKind, patch: unknown) => Promise<TView>;
  readonly findRow: (view: TView) => TRow | undefined;
  /** Optional: told about every settled attempt, for a preview's report or a
   *  page's feedback banner. Never used to decide anything here. */
  readonly onSettled?: (
    kind: PlanWriteKind,
    outcome: string,
    confirmed: TRow,
  ) => void;
}): PlanLineWrite<TRow> {
  const { initial, write, findRow, onSettled } = params;

  const [row, setRow] = useState<TRow>(initial);
  const [outcomes, setOutcomes] = useState<PlanLineOutcomes>(IDLE);
  const [inFlight, setInFlight] = useState<PlanWriteKind | null>(null);
  const [refused, setRefused] = useState<readonly PlanWriteRefusal[]>([]);
  const [stale, setStale] = useState<readonly PlanWriteKind[]>([]);

  /* SYNCHRONOUS, because the guard has to be read at the moment a handler
     fires. React state is one render behind, so two events in the same tick
     would both read "idle" and both fire -- which is a double-click. */
  const inFlightRef = useRef<PlanWriteKind | null>(null);
  const ticketRef = useRef(0);
  /* The confirmed row mirrored, so a reply reconciles from the row that
     STANDS rather than from one captured when the handler was created, and so
     the report can name it without a side effect inside a state updater. */
  const rowRef = useRef<TRow>(initial);

  // Completion and sheet refreshes also return confirmed rows through the
  // parent. Adopt those responses rather than freezing the first mounted row.
  useEffect(() => {
    rowRef.current = initial;
    setRow(initial);
  }, [initial]);

  const run = useCallback(
    async (kind: PlanWriteKind, patch: unknown): Promise<boolean> => {
      const already = inFlightRef.current;
      if (already !== null) {
        setRefused((prev) => [...prev, { kind, because: already }]);
        return false;
      }
      inFlightRef.current = kind;
      setInFlight(kind);
      ticketRef.current += 1;
      const ticket = ticketRef.current;
      setOutcomes((prev) => ({ ...prev, [kind]: { kind: 'saving' } }));

      /** A reply that is no longer the latest issued write's is DROPPED. */
      const superseded = (): boolean => {
        if (ticket === ticketRef.current) return false;
        setStale((prev) => [...prev, kind]);
        return true;
      };
      const settle = (outcome: PlanDecisionOutcome, label: string): void => {
        setOutcomes((prev) => ({ ...prev, [kind]: outcome }));
        onSettled?.(kind, label, rowRef.current);
      };

      try {
        const view = await write(kind, patch);
        if (superseded()) return false;
        const served = findRow(view);
        if (served === undefined) {
          /* THE REPLY CARRIED NO ROW FOR THIS ITEM. That is not a save: the
             write may or may not have happened, and the only honest thing on
             screen is the last value a server did confirm. */
          settle({ kind: 'failed', failure: 'unconfirmed' }, 'unconfirmed');
          return false;
        }
        rowRef.current = served;
        setRow(served);
        settle({ kind: 'confirmed' }, 'confirmed');
        return true;
      } catch (err) {
        if (superseded()) return false;
        const failure = classifyPlanWriteFailure(err);
        settle({ kind: 'failed', failure }, failure);
        return false;
      } finally {
        inFlightRef.current = null;
        setInFlight(null);
      }
    },
    [write, findRow, onSettled],
  );

  return { row, inFlight, outcomes, run, refused, stale, clearOutcomes: () => setOutcomes(IDLE) };
}
